import React from "react";
import { User, Phone, Mail, Calendar } from "lucide-react";
import { EmployeeInfo } from "../api";

interface UserProfileCardProps {
    employee: EmployeeInfo;
}

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";

    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;

    const day = d.getDate();
    const suffix =
        day % 10 === 1 && day !== 11
            ? "st"
            : day % 10 === 2 && day !== 12
                ? "nd"
                : day % 10 === 3 && day !== 13
                    ? "rd"
                    : "th";

    const month = d.toLocaleString("default", { month: "short" });

    return `${day}${suffix} ${month} ${d.getFullYear()}`;
};

const UserProfileCard: React.FC<UserProfileCardProps> = ({ employee }) => {
    return (
        <div className="group bg-gradient-to-br from-white to-gray-50 hover:from-gray-50 hover:to-white border border-gray-200 hover:border-gray-300 rounded-lg p-3.5 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex gap-4 items-center">
                {/* Profile Picture */}
                <div className="relative flex-shrink-0">
                    {employee.employee_photo_url ? (
                        <div className="relative">
                            <img
                                src={employee.employee_photo_url}
                                alt={`${employee.first_name} ${employee.last_name}`}
                                className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 group-hover:border-gray-300 transition-colors shadow-sm"
                            />
                            <div className="absolute inset-0 rounded-full ring-2 ring-transparent group-hover:ring-gray-200 transition-all duration-300"></div>
                        </div>
                    ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-200 group-hover:border-gray-300 flex items-center justify-center shadow-sm transition-all duration-300">
                            <User className="w-7 h-7 text-gray-400 group-hover:text-gray-500 transition-colors" strokeWidth={1.5} />
                        </div>
                    )}
                </div>

                {/* Employee Info */}
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base truncate">
                        {employee.first_name} {employee.last_name}
                    </h3>

                    <p className="text-sm text-gray-600 font-medium">
                        {employee.designation}
                    </p>

                    {/* <p className="text-xs text-gray-500 mt-0.5">
                        {employee.department}
                    </p> */}

                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 text-gray-400" strokeWidth={2} />
                        <span>Joined {formatDate(employee.date_of_joining)}</span>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="flex flex-col gap-1.5 text-sm min-w-[220px]">
                    <div className="flex items-center gap-2 group/item">
                        <div className="flex items-center justify-center w-5 h-5 rounded-md bg-blue-50 group-hover/item:bg-blue-100 transition-colors flex-shrink-0">
                            <Phone className="w-3 h-3 text-blue-600" strokeWidth={2} />
                        </div>
                        <span className="text-gray-700 font-medium">
                            {employee.mobile_number || "-"}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 group/item">
                        <div className="flex items-center justify-center w-5 h-5 rounded-md bg-purple-50 group-hover/item:bg-purple-100 transition-colors flex-shrink-0">
                            <Mail className="w-3 h-3 text-purple-600" strokeWidth={2} />
                        </div>
                        <span className="text-gray-700 font-medium truncate" title={employee.work_email || "None"}>
                            {employee.work_email || "None"}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfileCard;
